/**
 * 四則演算の計算フィールドの内訳を、式の形で作る（#3412）。
 *
 * 見せたいのは「どの値を使って計算したか」。
 * `({単価} - {値引}) * {数量}` なら、式の中の項目を値に置き換えて
 * 「(単価：1,000 - 値引：100) * 数量：3」と出す。
 *
 * IF・比較・文字列の結合などを含む式は、置き換えると読みにくくなるため対象外。
 * それらは formatCalculationBreakdown（値を並べる・比較の記号を出す）に任せる。
 */
import { CALCULATION_BREAKDOWN_SUM_EXPAND_MAX_ROWS } from '../../constants/enum';
import { calculationFunction } from '../calculation/constants';
import { tokenize, type Token } from '../calculation/tokenizer';
import {
  formatValue,
  LABEL_VALUE_SEPARATOR,
  sumLabel,
  summableNumbers,
  type BreakdownItem,
  type BreakdownValueSource,
} from './calculationBreakdown';

/** 四則演算として扱う演算子。& は文字列の結合、比較演算子は比較のため含めない */
const ARITHMETIC_OPERATOR_LIST = ['+', '-', '*', '/', '^'];

/** 置き換える範囲（元の式の位置）と、置き換え後の文字列 */
interface Replacement {
  position: number;
  end: number;
  text: string;
}

const labeled = (label: string, value: string): string =>
  `${label}${LABEL_VALUE_SEPARATOR}${value}`;

/**
 * SUM({明細.列}) の表示。
 * 式が SUM だけなら、行数が少ないときに「明細金額：100 + 200 + 300 = 600」と足し算を見せる。
 * 行数が多いときや、ほかの計算と組み合わせた式では合計だけを出す（1行に収めるため）。
 * 行数は空欄の行も含めた明細の行数（画面で見える行数と合わせる）。
 */
const formatSum = (itemName: string, values: string[], isWholeFormula: boolean): string => {
  const numbers = summableNumbers(values);
  const total = formatValue(String(numbers.reduce((sum, num) => sum + num, 0)));
  const totalText = labeled(sumLabel(itemName), total);
  if (!isWholeFormula) return totalText;

  if (numbers.length >= 2 && numbers.length <= CALCULATION_BREAKDOWN_SUM_EXPAND_MAX_ROWS) {
    const addends = numbers.map(num => formatValue(String(num))).join(' + ');
    return `${labeled(itemName, addends)} = ${total}`;
  }
  // 1行以下なら「（1行）」は見れば分かるため付けない
  return values.length >= 2 ? `${totalText}（${values.length}行）` : totalText;
};

/** SUM({明細.列}) の4トークンなら、その列の参照トークンを返す */
const sumReferenceAt = (tokens: Token[], index: number): Token | undefined => {
  const [func, lparen, ref, rparen] = tokens.slice(index, index + 4);
  const isSum =
    func?.type === 'funcName' &&
    func.value.toUpperCase() === calculationFunction.sum &&
    lparen?.type === 'lparen' &&
    ref?.type === 'fieldRef' &&
    rparen?.type === 'rparen';
  return isSum ? ref : undefined;
};

/**
 * 四則演算の式なら、項目を値に置き換えた式を返す。対象外の式は undefined。
 *
 * 対象は「項目参照・数値・+ - * / ^・括弧・SUM({明細.列})」だけでできた式。
 * 項目一覧に無い参照や、SUM の外で明細の列を参照している式も undefined にする
 * （値が決まらず、置き換えた式が実際の計算とずれるため）。
 */
export const formatArithmeticBreakdown = (
  formula: string,
  items: BreakdownItem[],
  source: BreakdownValueSource,
): string | undefined => {
  let tokens: Token[];
  try {
    tokens = tokenize(formula);
  } catch {
    return undefined;
  }
  if (!tokens.some(token => token.type === 'fieldRef')) return undefined;

  const byCode = new Map(items.map(item => [item.fieldCode, item]));
  const replacements: Replacement[] = [];

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    const sumRef = sumReferenceAt(tokens, i);
    if (sumRef !== undefined) {
      const item = byCode.get(sumRef.value);
      const values = source.getColumnValues(sumRef.value);
      if (item?.parentId === undefined || values === undefined) return undefined;
      const sumEnd = tokens[i + 3].end;
      const isWholeFormula = tokens.length === 4;
      replacements.push({
        position: token.position,
        end: sumEnd,
        text: formatSum(item.itemName, values, isWholeFormula),
      });
      i += 3;
      continue;
    }

    if (token.type === 'fieldRef') {
      const item = byCode.get(token.value);
      if (item === undefined || item.parentId !== undefined) return undefined;
      const value = formatValue(source.getSingleValue(token.value) ?? '');
      replacements.push({
        position: token.position,
        end: token.end,
        text: labeled(item.itemName, value),
      });
      continue;
    }

    const isArithmetic =
      token.type === 'number' ||
      token.type === 'lparen' ||
      token.type === 'rparen' ||
      (token.type === 'operator' && ARITHMETIC_OPERATOR_LIST.includes(token.value));
    if (!isArithmetic) return undefined;
  }

  // 置き換えない部分（演算子・数値・括弧・空白）は元の式の書き方をそのまま残す
  let result = '';
  let cursor = 0;
  for (const replacement of replacements) {
    result += formula.slice(cursor, replacement.position) + replacement.text;
    cursor = replacement.end;
  }
  return result + formula.slice(cursor);
};
